import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { validatePhone } from 'bl/validation/validatePhone.bl';
import CheckboxInput from 'components/form/CheckboxInput.component';
import EnumeratedInput from 'components/form/EnumeratedInput.component';
import FormError from 'components/form/FormError.component';
import FormField from 'components/form/FormField.component';
import IntegerInput from 'components/form/IntegerInput.component';
import MoneyInput from 'components/form/MoneyInput.component';
import PasswordInput from 'components/form/PasswordInput.component';
import TextInput from 'components/form/TextInput.component';
import Space from 'components/generic/Space.component';
import { RegisterPage as RegisterPageV2 } from 'components/onboarding/RegisterPage.component';
import { SalonSetupPage as SalonSetupPageV2 } from 'components/onboarding/SalonSetupPage.component';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import IP from 'constants/form/inputParser.const';
import BUSINESS_NAME from 'constants/salon/businessName.const';
import S from 'constants/string.const';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import { selectCognitoState } from 'utils/cognito.util';
import logger from 'utils/logger.util';

import CN from './OnboardingPage.module.scss';

const L = logger('OnboardingPage');

const OnboardingPage = () => {
  const handleValidate = useCallback((ev) => {
    const { number, name, is, parser, raw, ...extra } = ev ?? {};
    L.debug('handleValidate()', name, raw, number, is, extra, validatePhone({ value: raw }));

    if (name === 'phone' && !validatePhone({ value: raw })) return { error: 'Bad phone number' };
    if (parser === IP.text && raw === 'error') return { error: 'You typed error' };
    if (parser === IP.text && is.blank) return { error: `You must fill the ${name}` };
    if (parser === IP.int && is.empty) return { error: `You must fill the ${name}` };
    if (parser === IP.int && !is.int) return { error: 'Invalid number' };
    if (parser === IP.int && number < 0) return { error: 'Negative number' };

    return void 1;
  }, []);

  return (
    <div data-bem="PosPage" className={CN.component}>
      <p>A display of errors:</p>
      <div className={CN.horizontal}>
        <FormError tag="empty" />
        <FormError tag="e1" value={new Error('error 1')} />
        <FormError tag="e2" value={['multiple', 'messages', 'here']} />
        <FormError tag="e3" value={['multiple \n lines \n here']} />
      </div>

      <p>Form fields:</p>
      <div className={cn(CN.vertical, CN.panel)}>
        <FormField tag="f1" name="integer" input={IntegerInput} noNegative onValidate={handleValidate}>
          <FormField.Before>
            <code>FormField</code>
            <Space>1</Space>
            <Space>
              (with <code>IntegerInput</code>):
            </Space>
          </FormField.Before>
        </FormField>

        <FormField tag="f2" name="text" input={TextInput} onValidate={handleValidate}>
          <FormField.Before>
            <Space>{S.indexDown}</Space>
            <code>FormField</code> 2 (with <code>TextInput</code>):
          </FormField.Before>
        </FormField>

        <FormField tag="f3" name="money" input={MoneyInput} onValidate={handleValidate}>
          <FormField.After>
            <Space>{S.indexUp}</Space>
            <code>FormField</code> 3 (with <code>MoneyInput</code>):
          </FormField.After>
        </FormField>

        <FormField tag="f4" name="phone" input={PhoneInput} onValidate={handleValidate} />

        <FormField tag="f5" name="password" input={PasswordInput} onValidate={handleValidate} />

        <FormField tag="f6" name="toggler" input={CheckboxInput} onValidate={handleValidate}>
          Something to toggle
        </FormField>

        <FormField tag="f7" name="business" label="Business type" input={EnumeratedInput} options={BUSINESS_NAME} onValidate={handleValidate} />
      </div>

      <p>Simply inputs:</p>
      <div className={cn(CN.vertical, CN.panel, CN.gapped)}>
        <IntegerInput noNegative />
        <TextInput value="some text input" />
        <CheckboxInput value={1}>Some checkbox</CheckboxInput>
        <CheckboxInput value={false} label="Some other checkbox" />
        <MoneyInput value={3.14} />
        <PasswordInput value="some password" />
        <PhoneInput placeholder="+44 123 456 7890" />
        <EnumeratedInput placeholder="pick one" options={{ 1: 'one', 2: 'two', 3: 'three' }} />
        <EnumeratedInput
          options={[
            { value: 4, description: 'four' },
            { value: 4, description: 'five' },
          ]}
        />
        <EnumeratedInput options={[6, 7, 8, 9]} />
        <EnumeratedInput options={BUSINESS_NAME} />
      </div>

      <p>
        The new <code>RegisterPageV2</code> component:
      </p>
      <div className={CN.horizontal}>
        <RegisterPageV2 />
      </div>

      <p>
        The new <code>SalonSetupPageV2</code> component:
      </p>
      <div className={CN.horizontal}>
        <SalonSetupPageV2 />
      </div>
    </div>
  );
};

const OnboardingPageHoc = connect((state) => ({
  cognitoErrorMessage: getAnyOrVoid('cognito.error.message', state),
  config: getAnyOrVoid('cognito.config', state),
  cognitoState: selectCognitoState(state),
}))(OnboardingPage);

// noinspection JSUnusedGlobalSymbols
export default OnboardingPageHoc;
